import { mockCourses } from "../../mock/courses";
import axiosInstance from "./axiosInstance";
import endpoints from "./endpoints";
import { ChangePasswordParams } from "./types/auth/changePassword";
import { confirmForgotPasswordParams } from "./types/auth/confirmForgotPassword";
import { firstTimeLoginParams } from "./types/auth/firstTimeLogin";
import { forgotPasswordParams } from "./types/auth/forgotPassword";
import {
  CreateUpdateNoteBody,
  CreateUpdateNoteResponse,
  GetHomeAssignmentsParams,
  HomeAssignment,
  LogInteractionBody,
  UserAssignment,
  UserAssignmentQueryParams,
  UserAssignmentAcknowledgementBodyParams,
} from "./types/general/homeAssignment";
import {
  CreatePortalBodyParams,
  GetPortalsParams,
  SetVisiblePagesParams,
  UpdateAttribute,
  UpdatePortalBodyParams,
} from "./types/portalConfiguration/portal";
import {
  GetPortalGroupParams,
  portalGroupBody,
  PortalGroupBulkDeleteBodyParams,
} from "./types/portalGroup/portalGroup";

import {
  AssignmentCreateUpdateBodyParams,
  AssignmentVersionBodyParams,
  DdlAssignment,
  GetAllAssignmentsShortPaginatedResult,
  GetAssignmentParams,
  GetAssignmentResult,
  GetDdlAssignmentsParams,
  TranslateBodyParams,
  TranslateResult,
} from "./types/assignments/assignment";

import { loginUserParams, logoutParams } from "./types/auth/login";
import { createUserParams, createUserResultNode } from "./types/auth/signup";
import { UserEmailQueryParams, userPoolResult } from "./types/auth/userPool";
import {
  ValidateEmailLink,
  ValidateEmailLinkParams,
} from "./types/auth/validateEmailLink";
import {
  CertificateQueryParams,
  UserCertificatesPaginatedResult,
} from "./types/certificates/certificates";
import {
  DashboardActivityResult,
  DashboardAdminAssignmentsResult,
  DashboardAssignmentsResult,
  DashboardSessionResult,
  DashboardUserAssignmentsResult,
} from "./types/dashboard";
import {
  EmailTemplateBodyParams,
  EmailTemplateQueryParams,
  EmailTemplateRemoveBodyParams,
  EmailTemplateResult,
  EmailTemplateShortResult,
  EmailTemplatesPaginatedResult,
  EmailTemplateTypeQueryParams,
  EmailTemplateTypeResult,
  SendEmailBody,
} from "./types/emailTemplates";
import {
  FileHubByIdResult,
  FileHubCreateUpdateBody,
  FileHubCreateUpdateResult,
  FileHubDeleteResponseStatus,
  FileHubFilesQueryParams,
  GetFileHubFilesPaginatedResult,
  GetS3PresignedUrlsDownloadResponse,
} from "./types/fileHub/fileHub";
import { CreateUserBody, GetUserParams } from "./types/general/user";
import {
  findOrReplaceParams,
  ReplaceTranslationBodyParams,
  TranslationManageResult,
  UpdateTranslation,
} from "./types/portalConfiguration/translations";
import {
  GetS3PresignedUrlResult,
  PresignedUrlBodyParams,
} from "./types/portalConfiguration/uploadPhoto";
import {
  CreateUpdatePortalUserAttributeBodyParams,
  EditUserAttributesBodyParams,
  GetUserAttributesParams,
  ReorderAttributeParams,
} from "./types/portalConfiguration/userAttribute";
import {
  AssessmentReportQueryParams,
  AssessmentReportResult,
  assignmentActivityReportCompleteType,
  getReportTemplateDdlResult,
  GetReportTemplateResult,
  GetReportTemplatesPagintedResult,
  ReportTemplateBodyParams,
  ReportTemplateQueryParams,
  ReportTemplateRemoveBodyParams,
  userActivityReportCompleteType,
  userReportCompleteType,
} from "./types/reports/reports";
import {
  AdminGetUserAssignmentsPaginatedResult,
  AdminUpdateUserAssignmentBodyParams,
  CreateUserAssignmentsParams,
  EditUserAssignmentsParams,
  RemoveUserAssignmentsParams,
} from "./types/userAssignment/types";
import {
  AdvancedDdlQueryParams,
  AdvancedSearchBodyParams,
  ChangeUserEmailBodyParams,
  CreateUserBodyParams,
  GetPortalUserAttributesPaginatedResult,
  GetUserProfileResult,
  GetUserResult,
  UpdateProfileBodyParams,
  UpdateUserBodyParams,
  UserFilterQueryParams,
  UserPortalMappingsByEmailQueryParams,
  UserPortalMapping,
  UserQueryParams,
  GetRolesForUserResult,
} from "./types/users/users";
import { Translations } from "../../context/TranslationContext";
import {
  BootstrapResponse,
  BootstrapQueryParams,
  GuestBootstrapResponse,
} from "./types/general/bootstrap";
import { ReInviteUserParams } from "./types/auth/reInvite";
import {
  EditTrainingRecordAttemptQueryParams,
  EditTrainingRecordBody,
  EditTrainingRecordQueryParams,
  GetSingleTrainingRecordInfoParams,
  GetSingleTrainingRecordInfoResult,
  GetTrainingRecordsPaginatedResult,
  UserAssignmentAllTrainingRecordsPaginatedResult,
} from "./types/editTraniningRecords/editTrainingRecords";
import {
  CreateUpdateUserImportJobResult,
  GetTemplateQueryParams,
  GetTemplateResult,
  UserImportJobBody,
  UserImportJobDetailResult,
  UserImportJobExecuteQueryParams,
  UserImportJobExecuteResult,
  UserImportJobFailureLogItem,
  UserImportJobOverviewResult,
  UserImportJobReviewQueryParams,
  UserImportJobReviewResult,
  UserImportJobValidateResult,
} from "./types/users/userActions";
import {
  GetPortalAssignmentRuleDetailResult,
  GetPortalAssignmentRulesResult,
  PortalAssignmentRuleBodyParams,
  PortalAssignmentRuleBulkDeleteBodyParams,
  PortalAssignmentRuleCreateUpdateResult,
  PortalAssignmentRuleQueryParams,
  PortalAssignmentRuleReorderBodyParams,
} from "./types/rules/rules";

const USE_MOCK = false;

// Bootstrap
export const getBootstrap = async (
  params?: BootstrapQueryParams,
): Promise<BootstrapResponse> => {
  const response = await axiosInstance.get(endpoints.getBootstrap(), {
    params,
  });
  return response.data;
};

export const getBootstrapGuest = async (
  portalUrl: string,
): Promise<GuestBootstrapResponse> => {
  const response = await axiosInstance.get(endpoints.getBootstrapGuest(), {
    params: { portalUrl: portalUrl },
  });
  return response.data;
};

// Courses
export const logInteraction = async (
  portalId: string,
  userId: string,
  assignmentId: string,
  body: LogInteractionBody,
) => {
  const response = await axiosInstance.post(
    endpoints.logInteraction(portalId, userId, assignmentId),
    body,
  );

  return response.data;
};

export const getHomeAssignments = async (
  userId: string,
  portalUid: string,
  params: GetHomeAssignmentsParams,
): Promise<HomeAssignment[]> => {
  if (USE_MOCK) {
    console.log("Using mock assignments data");
    return new Promise((resolve) => {
      setTimeout(() => resolve(mockCourses), 100);
    });
  }

  const response = await axiosInstance.get(
    endpoints.getHomeAssignments(userId, portalUid),
    { params },
  );
  return response.data;
};

export const createNote = async (
  portalId: string,
  userId: string,
  assignmentId: string,
  body: CreateUpdateNoteBody,
): Promise<CreateUpdateNoteResponse> => {
  const response = await axiosInstance.post(
    endpoints.createNote(portalId, userId, assignmentId),
    body,
  );
  return response.data;
};

export const updateNote = async (
  portalId: string,
  userId: string,
  assignmentId: string,
  noteId: string,
  body: CreateUpdateNoteBody,
): Promise<CreateUpdateNoteResponse> => {
  const response = await axiosInstance.post(
    endpoints.updateNote(portalId, userId, assignmentId, noteId),
    body,
  );
  return response.data;
};

export const undoDeleteNote = async (
  portalId: string,
  userId: string,
  assignmentId: string,
  noteId: string,
) => {
  const response = await axiosInstance.post(
    endpoints.undoDeleteNote(portalId, userId, assignmentId, noteId),
  );
  return response.data;
};

export const submitAcknowledgement = async (
  portalId: string,
  userId: string,
  assignmentId: string,
  body: UserAssignmentAcknowledgementBodyParams,
) => {
  const response = await axiosInstance.post(
    endpoints.submitAcknowledgement(portalId, userId, assignmentId),
    body,
  );
  return response.data;
};

export const getUserAssignment = async (
  portalId: string,
  userId: string,
  assignmentId: string,
  params?: UserAssignmentQueryParams,
): Promise<UserAssignment> => {
  const response = await axiosInstance.get(
    endpoints.getUserAssignment(portalId, userId, assignmentId),
    { params },
  );
  return response.data;
};

export const getCertificates = async (
  portalId: string,
  userId: string,
  params?: CertificateQueryParams,
): Promise<UserCertificatesPaginatedResult> => {
  const response = await axiosInstance.get(
    endpoints.getCertificates(portalId, userId),
    { params },
  );
  return response.data;
};

// Auth
export const login = async (body: loginUserParams) => {
  const response = await axiosInstance.post(endpoints.login(), body);
  return response.data;
};

export const signup = async (
  body: createUserParams,
): Promise<createUserResultNode> => {
  const response = await axiosInstance.post(endpoints.signup(), body);
  return response.data;
};

export const createUser = async (
  body: CreateUserBody,
): Promise<createUserResultNode> => {
  const response = await axiosInstance.post(endpoints.createUser(), body);
  return response.data;
};

export const firstTimeLogin = async (body: firstTimeLoginParams) => {
  const response = await axiosInstance.post(endpoints.firstTimeLogin(), body);
  return response.data;
};

export const validateEmail = async (
  params: ValidateEmailLink,
): Promise<ValidateEmailLinkParams> => {
  const response = await axiosInstance.get(endpoints.validateEmail(), {
    params,
  });
  return response.data;
};

export const forgotPassword = async (body: forgotPasswordParams) => {
  const response = await axiosInstance.post(endpoints.forgotPassword(), body);
  return response.data;
};

export const confirmForgotPassword = async (
  body: confirmForgotPasswordParams,
) => {
  const response = await axiosInstance.post(
    endpoints.confirmForgotPassword(),
    body,
  );
  return response.data;
};

export const changePassword = async (body: ChangePasswordParams) => {
  const response = await axiosInstance.post(endpoints.changePassword(), body);
  return response.data;
};

export const logout = async (body: logoutParams) => {
  const reponse = await axiosInstance.post(endpoints.logout(), body);
  return reponse.data;
};

export const getUser = async (params: GetUserParams) => {
  const response = await axiosInstance.get(endpoints.getUser(), {
    params,
  });
  return response.data;
};

export const getUserPool = async (
  params: UserEmailQueryParams,
): Promise<userPoolResult> => {
  const response = await axiosInstance.get(endpoints.getUserPool(), { params });
  return response.data;
};

export const reInviteUser = async (params: ReInviteUserParams) => {
  const response = await axiosInstance.post(endpoints.reInviteUser(), params);
  return response.data;
};

// Portal configuration
export const getPortals = async (params: GetPortalsParams) => {
  const response = await axiosInstance.get(endpoints.getPortals(), { params });
  return response.data;
};

export const addPortal = async (body: CreatePortalBodyParams) => {
  const response = await axiosInstance.post(endpoints.addPortal(), body);
  return response.data;
};

export const updatePortal = async (
  portalId: string,
  body: UpdatePortalBodyParams,
) => {
  const response = await axiosInstance.post(
    endpoints.updatePortal(portalId),
    body,
  );
  return response.data;
};

// Email templates and sending
export const getEmailTemplates = async (
  portalId: string,
  params?: EmailTemplateQueryParams,
): Promise<EmailTemplatesPaginatedResult | EmailTemplateShortResult[]> => {
  const response = await axiosInstance.get(
    endpoints.getEmailTemplates(portalId),
    { params },
  );
  return response.data;
};

export const getEmailTemplate = async (
  portalId: string,
  templateId: string,
): Promise<EmailTemplateResult> => {
  const response = await axiosInstance.get(
    endpoints.getEmailTemplate(portalId, templateId),
  );
  return response.data;
};

export const createUpdateEmailTemplate = async (
  portalId: string,
  template: EmailTemplateBodyParams,
): Promise<EmailTemplateResult> => {
  const response = await axiosInstance.post(
    endpoints.createUpdateEmailTemplate(portalId),
    template,
  );
  return response.data;
};

export const deleteEmailTemplate = async (
  portalId: string,
  body: EmailTemplateRemoveBodyParams,
): Promise<void> => {
  await axiosInstance.post(endpoints.deleteEmailTemplate(portalId), body);
};

export const getEmailTemplateTypes = async (
  portalId: string,
  params?: EmailTemplateTypeQueryParams,
): Promise<EmailTemplateTypeResult[]> => {
  const response = await axiosInstance.get(
    endpoints.getEmailTemplateTypes(portalId),
    { params },
  );
  return response.data;
};

export const sendEmail = async (
  portalId: string,
  body: SendEmailBody,
): Promise<any> => {
  const response = await axiosInstance.post(
    endpoints.sendEmail(portalId),
    body,
  );
  return response.data;
};

// User Assignments
/**
 * Create assignments for selected users
 */
export const createUserAssignments = async (
  portalId: string,
  body: CreateUserAssignmentsParams,
): Promise<any> => {
  const response = await axiosInstance.post(
    endpoints.createUserAssignments(portalId),
    body,
  );
  return response.data;
};

/**
 * Edit assignments for selected users
 */
export const editUserAssignments = async (
  portalId: string,
  body: EditUserAssignmentsParams,
): Promise<any> => {
  const response = await axiosInstance.post(
    endpoints.editUserAssignments(portalId),
    body,
  );
  return response.data;
};

/**
 * Remove assignments for selected users
 */
export const removeUserAssignments = async (
  portalId: string,
  body: RemoveUserAssignmentsParams,
): Promise<void> => {
  await axiosInstance.post(endpoints.removeUserAssignments(portalId), body);
};

/**
 * Edit assignment status for selected users
 */
export const editUserAssignmentStatus = async (
  portalId: string,
  body: AdminUpdateUserAssignmentBodyParams,
): Promise<void> => {
  await axiosInstance.post(endpoints.editUserAssignmentStatus(portalId), body);
};

export const getPortal = async (portalId: string) => {
  const response = await axiosInstance.get(endpoints.getPortal(portalId));
  return response.data;
};

// User attributes
export const addUpdateUserAttribute = async (
  portalId: string,
  body: CreateUpdatePortalUserAttributeBodyParams,
) => {
  const response = await axiosInstance.post(
    endpoints.addUserAttribute(portalId),
    body,
  );
  return response.data;
};

export const getUserAttribute = async (
  portalId: string,
  userAttributeId: string,
  languageCode: string,
) => {
  const response = await axiosInstance.get(
    endpoints.getUserAttribute(portalId, userAttributeId),
    { params: { languageCode } },
  );
  return response.data;
};

export const getUserAttributes = async (
  portalId: string,
  params: GetUserAttributesParams,
) => {
  const response = await axiosInstance.get(
    endpoints.getUserAttributes(portalId),
    { params },
  );
  return response.data;
};

export const reorderAttributes = async (
  portalId: string,
  userAttributeId: string,
  body: ReorderAttributeParams,
) => {
  const response = await axiosInstance.post(
    endpoints.reorderAttributes(portalId, userAttributeId),
    body,
  );
  return response.data;
};

export const getAlllanguages = async () => {
  const response = await axiosInstance.get(endpoints.getAlllanguages());
  return response.data;
};

// Portal pages
export const getAllPages = async () => {
  const response = await axiosInstance.get(endpoints.getAllPages());
  return response.data;
};

export const getAllPagesForPortal = async (portalId: string) => {
  const response = await axiosInstance.get(
    endpoints.getAllPagesForPortal(portalId),
  );
  return response.data;
};

export const setVisiblePageForPortal = async (
  portalId: string,
  body: SetVisiblePagesParams,
) => {
  const response = await axiosInstance.post(
    endpoints.setVisiblePageForPortal(portalId),
    body,
  );
  return response.data;
};

// Portal design
export const getPageDesign = async (portalId: string, pageId: string) => {
  const response = await axiosInstance.get(
    endpoints.getPageDesign(portalId, pageId),
  );
  return response.data;
};

export const getDesignByUrl = async (pageId: string, portalUrl: string) => {
  const response = await axiosInstance.get(endpoints.getDesignByUrl(pageId), {
    params: { portalUrl },
  });
  return response.data;
};

export const getSectionDesign = async (
  portalId: string,
  pageId: string,
  pageSectionId: string,
) => {
  const response = await axiosInstance.get(
    endpoints.getSectionDesign(portalId, pageId, pageSectionId),
  );
  return response.data;
};

export const updateSectionDesign = async (
  portalId: string,
  pageId: string,
  pageSectionId: string,
  body: UpdateAttribute,
) => {
  const response = await axiosInstance.post(
    endpoints.updateSectionDesign(portalId, pageId, pageSectionId),
    body,
  );
  return response.data;
};

// Assignments
export const getS3PresignedUrl = async (
  portalId: string,
  body: PresignedUrlBodyParams,
): Promise<GetS3PresignedUrlResult> => {
  const response = await axiosInstance.post(
    endpoints.getS3PresignedUrl(portalId),
    body,
  );
  return response.data;
};

export const getS3PresignedUrlsDownload = async (
  portalId: string,
  body: PresignedUrlBodyParams,
): Promise<GetS3PresignedUrlsDownloadResponse> => {
  const response = await axiosInstance.post(
    endpoints.getS3PresignedUrl(portalId),
    body,
  );
  return response.data;
};

// Portal translation
export const getAllPortalTranslations = async (
  portalId: string,
  langId: string,
): Promise<Translations> => {
  const response = await axiosInstance.get(
    endpoints.getAllPortalTranslations(portalId, langId),
  );
  return response.data;
};

export const getTranslationsManage = async (
  portalId: string,
  tabId: string,
  tabSectionId: string,
): Promise<TranslationManageResult> => {
  const response = await axiosInstance.get(
    endpoints.getTranslationsManage(portalId, tabId, tabSectionId),
  );
  return response.data;
};

export const deleteTranslationOverride = async (
  portalId: string,
  tabId: string,
  tabSectionId: string,
  attributeId: string,
  langId?: string,
) => {
  const response = await axiosInstance.post(
    endpoints.deleteTranslationOverride(
      portalId,
      tabId,
      tabSectionId,
      attributeId,
    ),
    langId ? { lang: langId } : {},
  );
  return response.data;
};

export const updateAttributeTranslation = async (
  portalId: string,
  tabId: string,
  tabSectionId: string,
  body: UpdateTranslation,
) => {
  const response = await axiosInstance.post(
    endpoints.updateAttributeTranslation(portalId, tabId, tabSectionId),
    body,
  );
  return response.data;
};

export const findOrTranslate = async (
  portalId: string,
  langId: string,
  params: findOrReplaceParams,
) => {
  const response = await axiosInstance.get(
    endpoints.findOrTranslate(portalId, langId),
    { params },
  );
  return response.data;
};

export const replaceTranslation = async (
  portalId: string,
  langId: string,
  body: ReplaceTranslationBodyParams,
) => {
  const response = await axiosInstance.post(
    endpoints.replaceTranslation(portalId, langId),
    body,
  );
  return response.data;
};

// Users management
export const getUsers = async (portalId: string, params: UserQueryParams) => {
  const response = await axiosInstance.get(endpoints.getUsers(portalId), {
    params,
  });
  return response.data;
};

export const getUserFilters = async (
  portalId: string,
  params: UserFilterQueryParams,
) => {
  const response = await axiosInstance.get(endpoints.getUserFilters(portalId), {
    params,
  });
  return response.data;
};

export const createNewUser = async (
  portalId: string,
  body: CreateUserBodyParams,
) => {
  const response = await axiosInstance.post(
    endpoints.createNewUser(portalId),
    body,
  );
  return response.data;
};

export const updateUser = async (
  portalId: string,
  body: UpdateUserBodyParams,
) => {
  const response = await axiosInstance.post(
    endpoints.updateUser(portalId),
    body,
  );
  return response.data;
};

export const changeUserEmail = async (body: ChangeUserEmailBodyParams) => {
  const response = await axiosInstance.post(endpoints.changeUserEmail(), body);
  return response.data;
};

export const getPortalUserAttribute = async (
  portalId: string,
  languageCode: string,
  short: boolean,
): Promise<GetPortalUserAttributesPaginatedResult> => {
  const response = await axiosInstance.get(
    endpoints.getPortalUserAttribute(portalId),
    { params: { languageCode, short } },
  );
  return response.data;
};

export const getAssignments = async (
  portalId: string,
  params: GetAssignmentParams,
) => {
  const { data } = await axiosInstance.get(endpoints.getAssignments(portalId), {
    params,
  });
  return data;
};

export const getDdlAssignments = async (
  portalId: string,
  params?: GetDdlAssignmentsParams,
): Promise<DdlAssignment[]> => {
  const { data } = await axiosInstance.get(
    endpoints.getDdlAssignments(portalId),
    { params },
  );
  return data;
};

export const getPortalAssignments = async (
  portalId: string,
  params: GetAssignmentParams,
): Promise<GetAllAssignmentsShortPaginatedResult> => {
  const { data } = await axiosInstance.get(endpoints.getAssignments(portalId), {
    params,
  });
  return data;
};

export const getAssignment = async (
  portalId: string,
  assignmentId: string,
): Promise<GetAssignmentResult> => {
  const response = await axiosInstance.get(
    endpoints.getAssignment(portalId, assignmentId),
  );
  return response.data;
};

export const translateTitle = async (
  body: TranslateBodyParams,
): Promise<TranslateResult> => {
  const response = await axiosInstance.post(endpoints.translateTitle(), body);
  return response.data;
};

export const addUpdateAssignment = async (
  portalId: string,
  body: AssignmentCreateUpdateBodyParams,
) => {
  const response = await axiosInstance.post(
    endpoints.addupdateAssignment(portalId),
    body,
  );
  return response.data;
};

export const getPrerequisiteSummaryResult = async (
  portalId: string,
  assignmentId: string,
) => {
  const response = await axiosInstance.get(
    endpoints.getPrerequisiteSummaryResult(portalId, assignmentId),
  );
  return response.data;
};

export const createUpdateAssignmentVersion = async (
  portalId: string,
  assignmentId: string,
  body: AssignmentVersionBodyParams,
) => {
  const response = await axiosInstance.post(
    endpoints.createUpdateAssignmentVersion(portalId, assignmentId),
    body,
  );
  return response.data;
};

export const deleteAssignmentVersion = async (
  portalId: string,
  assignmentId: string,
  versionId: string,
) => {
  const response = await axiosInstance.delete(
    endpoints.deleteAssignmentVersion(portalId, assignmentId, versionId),
  );
  return response.data;
};

export const updateIsAssigned = async (
  portalId: string,
  assignmentId: string,
  isAssigned: boolean,
) => {
  const response = await axiosInstance.post(
    endpoints.updateIsAssigned(portalId, assignmentId),
    { isAssigned },
  );
  return response.data;
};

export const advancedUserSearch = async (
  portalId: string,
  body: AdvancedSearchBodyParams,
) => {
  const response = await axiosInstance.post(
    endpoints.advancedUserSearch(portalId),
    body,
  );
  return response.data;
};

export const getPortalGroups = async (
  portalId: string,
  params: GetPortalGroupParams,
) => {
  const response = await axiosInstance.get(
    endpoints.getPortalGroups(portalId),
    { params },
  );
  return response.data;
};

export const bulkDeletePortalGroups = async (
  portalId: string,
  body: PortalGroupBulkDeleteBodyParams,
) => {
  const response = await axiosInstance.post(
    endpoints.bulkDeletePortalGroups(portalId),
    body,
  );

  return response.data;
};

export const createUpdatePortalGroup = async (
  portalId: string,
  body: portalGroupBody,
) => {
  const response = await axiosInstance.post(
    endpoints.createUpdatePortalGroup(portalId),
    body,
  );
  return response.data;
};

export const getAdvancedUserAttributesDDL = async (
  portalId: string,
  params: AdvancedDdlQueryParams,
) => {
  const response = await axiosInstance.get(
    endpoints.getAdvancedUserAttributesDDL(portalId),
    { params },
  );
  return response.data;
};

export const addEditUserAttribute = async (
  portalId: string,
  body: EditUserAttributesBodyParams,
) => {
  const response = await axiosInstance.post(
    endpoints.addEditUserAttribute(portalId),
    body,
  );
  return response.data;
};

export const bulkUpdateStatus = async (
  portalId: string,
  body: { users: string[]; adminUser: string },
) => {
  const response = await axiosInstance.post(
    endpoints.bulkUpdateStatus(portalId),
    body,
  );
  return response.data;
};

export const getAllUserAssignments = async (
  portalId: string,
  body: {
    users: string[];
    queryParams?: {
      search?: string;
      status?: string;
      limit?: number;
      offset?: number;
      orderBy?: string;
      sort?: string;
    };
  },
): Promise<AdminGetUserAssignmentsPaginatedResult> => {
  const response = await axiosInstance.post(
    endpoints.getAllUserAssignments(portalId),
    {
      users: body.users,
      queryParams: body.queryParams || {},
    },
  );
  return response.data;
};

export const getUserWithAllDetails = async (
  portalId: string,
  userId: string,
): Promise<GetUserResult> => {
  const response = await axiosInstance.get(
    endpoints.getUserWithAllDetails(portalId, userId),
  );

  return response.data;
};

export const getRolesForUser = async (
  portalId: string,
): Promise<GetRolesForUserResult> => {
  const response = await axiosInstance.get(endpoints.getRolesForUser(portalId));
  return response.data;
};

export const getUserPortalMappingsByEmail = async (
  portalId: string,
  params: UserPortalMappingsByEmailQueryParams,
): Promise<UserPortalMapping[]> => {
  const response = await axiosInstance.get(
    endpoints.getUserPortalMappingsByEmail(portalId),
    { params },
  );

  return response.data;
};

export const getMyProfile = async (
  portalId: string,
  userId: string,
): Promise<GetUserProfileResult> => {
  const response = await axiosInstance.get(
    endpoints.getMyProfile(portalId, userId),
  );
  return response.data;
};

export const updateMyProfile = async (
  portalId: string,
  userId: string,
  body: UpdateProfileBodyParams,
): Promise<GetUserProfileResult> => {
  const response = await axiosInstance.post(
    endpoints.updateMyProfile(portalId, userId),
    body,
  );
  return response.data;
};

export const updateUserLanguage = async (
  portalId: string,
  body: { languageCode: string },
): Promise<{ languageCode: string }> => {
  const response = await axiosInstance.post(
    endpoints.updateUserLanguage(portalId),
    body,
  );
  return response.data;
};

// Bulk user actions (userImportJob)
export const getTemplate = async (
  portalId: string,
  params: GetTemplateQueryParams,
): Promise<GetTemplateResult> => {
  const response = await axiosInstance.get(endpoints.getTemplate(portalId), {
    params,
  });
  return response.data;
};

export const createUpdateUserImportJob = async (
  portalId: string,
  body: UserImportJobBody,
): Promise<CreateUpdateUserImportJobResult> => {
  const response = await axiosInstance.post(
    endpoints.createUpdateTemplate(portalId),
    body,
  );
  return response.data;
};

export const validateUserImportJob = async (
  portalId: string,
  jobId: string,
): Promise<UserImportJobValidateResult> => {
  const response = await axiosInstance.get(
    endpoints.validateTemplate(portalId, jobId),
  );
  return response.data;
};

export const downloadUserImportJobValidationLog = async (
  portalId: string,
  jobId: string,
): Promise<UserImportJobFailureLogItem[]> => {
  const response = await axiosInstance.get(
    endpoints.downloadValidationLog(portalId, jobId),
  );
  return response.data;
};

export const getUserImportJobReview = async (
  portalId: string,
  jobId: string,
  params: UserImportJobReviewQueryParams,
): Promise<UserImportJobReviewResult> => {
  const response = await axiosInstance.get(
    endpoints.reviewTemplate(portalId, jobId),
    { params },
  );
  return response.data;
};

export const getUserImportJobDetail = async (
  portalId: string,
  jobId: string,
  recordId: string,
): Promise<UserImportJobDetailResult> => {
  const response = await axiosInstance.get(
    endpoints.userImportJobDetail(portalId, jobId, recordId),
  );
  return response.data;
};

export const executeUserImportJob = async (
  portalId: string,
  jobId: string,
  params: UserImportJobExecuteQueryParams,
): Promise<UserImportJobExecuteResult> => {
  const response = await axiosInstance.post(
    endpoints.executeUserImportJob(portalId, jobId),
    undefined,
    { params },
  );
  return response.data;
};

export const getUserImportJobOverview = async (
  portalId: string,
  jobId: string,
): Promise<UserImportJobOverviewResult> => {
  const response = await axiosInstance.get(
    endpoints.userImportJobOverview(portalId, jobId),
  );
  return response.data;
};

// Reports
export const getAllReportTemplates = async (
  portalId: string,
  params: ReportTemplateQueryParams,
): Promise<GetReportTemplatesPagintedResult> => {
  const response = await axiosInstance.get(
    endpoints.getAllReportTemplates(portalId),
    { params },
  );
  return response.data;
};

export const getReportTemplateDdl = async (
  portalId: string,
): Promise<getReportTemplateDdlResult> => {
  const response = await axiosInstance.get(
    endpoints.getReportTemplateDdl(portalId),
  );
  return response.data;
};

export const createUpdateReportTemplate = async (
  portalId: string,
  body: ReportTemplateBodyParams,
) => {
  const response = await axiosInstance.post(
    endpoints.createUpdateReportTemplate(portalId),
    body,
  );

  return response.data;
};

export const getReportTemplateById = async (
  portalId: string,
  reportTemplateId: string,
): Promise<GetReportTemplateResult> => {
  const response = await axiosInstance.get(
    endpoints.getReportTemplateById(portalId, reportTemplateId),
  );

  return response.data;
};

export const deleteReportTemplate = async (
  portalId: string,
  body: ReportTemplateRemoveBodyParams,
): Promise<void> => {
  await axiosInstance.post(endpoints.deleteReportTemplate(portalId), body);
};

export const runReportTemplate = async (
  portalId: string,
  reportTemplateId: string,
  adminUser: string,
): Promise<
  | userReportCompleteType
  | assignmentActivityReportCompleteType
  | userActivityReportCompleteType
> => {
  const response = await axiosInstance.post(
    endpoints.runReportTemplate(portalId, reportTemplateId),
    { adminUser },
  );

  return response.data;
};

export const previewReportTemplate = async (
  portalId: string,
  body: ReportTemplateBodyParams,
): Promise<any> => {
  const response = await axiosInstance.post(
    endpoints.previewReportTemplate(portalId),
    body,
  );

  return response.data;
};

export const getAssesmentReportData = async (
  portalId: string,
  params: AssessmentReportQueryParams,
): Promise<AssessmentReportResult> => {
  const response = await axiosInstance.get(
    endpoints.getAssesmentReportData(portalId),
    { params },
  );

  return response.data;
};

// Dashboard
export const getDashboardAssignments = async (
  portalId: string,
): Promise<DashboardAssignmentsResult> => {
  const response = await axiosInstance.get(
    endpoints.getDashboardAssignments(portalId),
  );
  return response.data;
};

export const getDashboardSessions = async (
  portalId: string,
): Promise<DashboardSessionResult[]> => {
  const response = await axiosInstance.get(
    endpoints.getDashboardSessions(portalId),
  );
  return response.data;
};

export const getDashboardActivities = async (
  portalId: string,
): Promise<DashboardActivityResult[]> => {
  const response = await axiosInstance.get(
    endpoints.getDashboardActivities(portalId),
  );
  return response.data;
};

export const getDashboard = async (
  portalId: string,
  userId?: string,
): Promise<
  DashboardAdminAssignmentsResult | DashboardUserAssignmentsResult
> => {
  const response = await axiosInstance.get(
    endpoints.getDashboard(portalId, userId),
  );
  return response.data;
};

// File Hub
export const getAllFileHubFiles = async (
  portalId: string,
  params: FileHubFilesQueryParams,
): Promise<GetFileHubFilesPaginatedResult> => {
  const response = await axiosInstance.get(
    endpoints.getAllFileHubFiles(portalId),
    { params },
  );
  return response.data;
};

export const getFileHubById = async (
  portalId: string,
  fileHubId: string,
): Promise<FileHubByIdResult> => {
  const response = await axiosInstance.get(
    endpoints.getFileHubById(portalId, fileHubId),
  );
  return response.data;
};

export const deleteFileHubFiles = async (
  portalId: string,
  body: string[],
): Promise<FileHubDeleteResponseStatus> => {
  const response = await axiosInstance.post(
    endpoints.deleteFileHubFiles(portalId),
    { fileHubList: body },
  );
  return response.data;
};

export const createOrUpdateFileHub = async (
  portalId: string,
  body: FileHubCreateUpdateBody,
): Promise<FileHubCreateUpdateResult> => {
  const response = await axiosInstance.post(
    endpoints.fileHubCreateUpdate(portalId),
    body,
  );
  return response.data;
};

export const subscribeForEmailNotification = async (
  portalId: string,
  subscribe: boolean,
): Promise<any> => {
  const response = await axiosInstance.post(
    endpoints.subscribeForEmailNotification(portalId),
    { subscribe },
  );
  return response.data;
};

// Edit Training Record APIs
export const getAllTrainingRecordsPaginated = async (
  portalId: string,
  params: EditTrainingRecordQueryParams,
): Promise<GetTrainingRecordsPaginatedResult> => {
  const response = await axiosInstance.get(
    endpoints.getAllTrainingRecordsPaginated(portalId),
    { params },
  );
  return response.data;
};

export const getSingleTrainingRecordInfo = async (
  portalId: string,
  params: GetSingleTrainingRecordInfoParams,
): Promise<GetSingleTrainingRecordInfoResult> => {
  const response = await axiosInstance.get(
    endpoints.getTrainingRecordById(portalId),
    { params },
  );
  return response.data;
};

export const GetUserAssignmentAllTrainingRecordsPaginatedResult = async (
  portalId: string,
  params: EditTrainingRecordAttemptQueryParams,
): Promise<UserAssignmentAllTrainingRecordsPaginatedResult> => {
  const response = await axiosInstance.get(
    endpoints.getUserAssignmentAllTrainingRecordsPaginatedResult(portalId),
    { params },
  );
  return response.data;
};

export const editTrainingRecord = async (
  portalId: string,
  recordId: string,
  body: EditTrainingRecordBody,
): Promise<void> => {
  await axiosInstance.post(
    endpoints.editTrainingRecord(portalId, recordId),
    body,
  );
};

export const resetTrainingRecord = async (
  portalId: string,
  recordId: string,
): Promise<any> => {
  const response = await axiosInstance.post(
    endpoints.resetTrainingRecord(portalId, recordId),
  );
  return response.data;
};

// Rules APIs
export const getAllRules = async (
  portalId: string,
  assignmentId: string,
  params: PortalAssignmentRuleQueryParams,
): Promise<GetPortalAssignmentRulesResult> => {
  const response = await axiosInstance.get(
    endpoints.getAllRules(portalId, assignmentId),
    { params },
  );
  return response.data;
};

export const reorderRules = async (
  portalId: string,
  assignmentId: string,
  body: PortalAssignmentRuleReorderBodyParams,
) => {
  const response = await axiosInstance.post(
    endpoints.reorderRules(portalId, assignmentId),
    body,
  );
  return response.data;
};

export const getRuleById = async (
  portalId: string,
  assignmentId: string,
  ruleId: string,
): Promise<GetPortalAssignmentRuleDetailResult> => {
  const response = await axiosInstance.get(
    endpoints.getRuleById(portalId, assignmentId, ruleId),
  );
  return response.data;
};

export const createUpdateRule = async (
  portalId: string,
  assignmentId: string,
  body: PortalAssignmentRuleBodyParams,
): Promise<PortalAssignmentRuleCreateUpdateResult> => {
  const response = await axiosInstance.post(
    endpoints.createUpdateRule(portalId, assignmentId),
    body,
  );
  return response.data;
};

export const deleteRules = async (
  portalId: string,
  assignmentId: string,
  body: PortalAssignmentRuleBulkDeleteBodyParams,
): Promise<any> => {
  const response = await axiosInstance.delete(
    endpoints.deleteRules(portalId, assignmentId),
    { data: body },
  );
  return response.data;
};
